import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import CustomPaginator from '../../components/CustomPaginator';
import StudentIssuesSkeletonLoader from '../../skeletons/StudentIssuesSkeletonLoader';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import ViewStudentIssueModal from './parts/ViewStudentIssueModal';

interface IssueData {
  id: number;
  student_id: number;
  teacher_id: number;
  session_title: string;
  issue_type: string;
  description: string;
  resolved: number;
  resolved_time: string | null;
  user_viewed: number;
  user_viewed_time: string | null;
  user_notified: number;
  user_notified_time: string;
  created_at: string;
  updated_at: string;
  student_name: string;
  class_id: string;
  section: string | null;
  roll_no: number;
  student_father_contact: string;
  student_mother_contact: string;
  teacher_name: string;
  teacher_contact: string;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

const StudentIssues: React.FC = () => {
  const [issues, setIssues] = useState<IssueData[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
  
  const toast = useRef<Toast>(null);

  useEffect(() => {
    fetchIssues();
  }, [page]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin-student-issues-list', {
        params: {
          page: page,
          limit: 10,
        },
      });

      const data = response.data;

      if (data.status === 'success') {
        const minLoadingTime = new Promise(resolve => setTimeout(resolve, 800));
        await minLoadingTime;

        setIssues(data.data.issues || []);
        setPaginationData(data.data.pagination || {
          current_page: 1,
          per_page: 10,
          total_items: 0,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        });
        setLoading(false);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: data.message || 'Failed to fetch student issues',
          life: 4000
        });
        setLoading(false);
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch student issues';
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: message,
        life: 5000
      });
      setLoading(false);
    }
  };

  const handleView = (issueId: number) => {
    setSelectedIssueId(issueId);
    setIsViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsViewModalOpen(false);
    setSelectedIssueId(null);
  };

  const getIssueTypeStyle = (issueType: string) => {
    switch (issueType.toLowerCase()) {
      case 'academic':
        return {
          bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.08) 100%)',
          border: 'rgba(59, 130, 246, 0.2)',
          text: '#2563eb',
          dot: '#3b82f6'
        };
      case 'attendance':
        return {
          bg: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(147, 51, 234, 0.08) 100%)',
          border: 'rgba(168, 85, 247, 0.2)',
          text: '#9333ea',
          dot: '#a855f7'
        };
      case 'health':
        return {
          bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)',
          border: 'rgba(16, 185, 129, 0.2)',
          text: '#059669',
          dot: '#10b981'
        };
      case 'behavioral':
        return {
          bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 7, 0.08) 100%)',
          border: 'rgba(245, 158, 11, 0.2)',
          text: '#d97706',
          dot: '#f59e0b'
        };
      default:
        return {
          bg: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.08) 100%)',
          border: 'rgba(107, 114, 128, 0.2)',
          text: '#4b5563',
          dot: '#6b7280'
        };
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const serialNumberTemplate = (data: IssueData, options: ColumnBodyOptions) => {
    const index = options.rowIndex + ((page - 1) * paginationData.per_page);
    return (
      <div style={{
        width: '28px',
        height: '28px',
        background: '#f9fafb',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '12px',
        color: '#6b7280',
        margin: '0 auto'
      }}>
        {index + 1}
      </div>
    );
  };

  const titleTemplate = (rowData: IssueData) => (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontWeight: '600',
        fontSize: '13px',
        color: '#1f2937',
        marginBottom: '4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {rowData.session_title}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#9ca3af',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '300px'
      }}>
        {rowData.description}
      </div>
    </div>
  );

  const issueTypeTemplate = (rowData: IssueData) => {
    const typeStyle = getIssueTypeStyle(rowData.issue_type);
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        background: typeStyle.bg,
        border: `1px solid ${typeStyle.border}`
      }}>
        <div style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: typeStyle.dot,
          boxShadow: `0 0 6px ${typeStyle.dot}`
        }}></div>
        <span style={{
          fontSize: '11px',
          color: typeStyle.text,
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {rowData.issue_type}
        </span>
      </div>
    );
  };

  const studentTemplate = (rowData: IssueData) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '600',
        color: 'white',
        flexShrink: 0
      }}>
        {rowData.student_name.charAt(0).toUpperCase()}
      </div>
      <div style={{
        fontSize: '13px',
        color: '#1f2937',
        fontWeight: '500'
      }}>
        {rowData.student_name}
      </div>
    </div>
  );

  const teacherTemplate = (rowData: IssueData) => (
    <div style={{
      fontSize: '13px',
      color: '#1f2937',
      fontWeight: '500'
    }}>
      {rowData.teacher_name}
    </div>
  );

  const dateTimeTemplate = (rowData: IssueData) => {
    const dateTime = formatDateTime(rowData.created_at);
    return (
      <div>
        <div style={{
          fontSize: '13px',
          color: '#1f2937',
          fontWeight: '500',
          marginBottom: '2px'
        }}>
          {dateTime.date}
        </div>
        <div style={{
          fontSize: '11px',
          color: '#9ca3af',
          fontFamily: 'monospace'
        }}>
          {dateTime.time}
        </div>
      </div>
    );
  };

  const statusTemplate = (rowData: IssueData) => {
    const isResolved = rowData.resolved === 1;
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        background: isResolved 
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.08) 100%)',
        border: isResolved 
          ? '1px solid rgba(16, 185, 129, 0.2)'
          : '1px solid rgba(239, 68, 68, 0.2)'
      }}>
        <div style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: isResolved ? '#10b981' : '#ef4444',
          boxShadow: `0 0 6px ${isResolved ? '#10b981' : '#ef4444'}`
        }}></div>
        <span style={{
          fontSize: '11px',
          color: isResolved ? '#059669' : '#dc2626',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {isResolved ? 'Resolved' : 'Pending'}
        </span>
      </div>
    );
  };

  const actionTemplate = (rowData: IssueData) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-rounded"
        tooltip="View"
        tooltipOptions={{ position: 'top' }}
        onClick={() => handleView(rowData.id)}
        style={{ color: '#60a5fa', width: '32px', height: '32px' }}
      />
      {/* <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: '32px', height: '32px' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: '32px', height: '32px' }}
      /> */}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .p-datatable .p-datatable-thead > tr > th {
            background: #f9fafb !important;
            color: #6b7280 !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.8px !important;
            padding: 16px 12px !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding: 16px 12px !important;
            border-bottom: 1px solid #f3f4f6 !important;
          }
          .p-datatable .p-datatable-tbody > tr:hover {
            background: linear-gradient(to right, rgba(65, 105, 225, 0.04) 0%, rgba(65, 105, 225, 0.02) 100%) !important;
            box-shadow: inset 4px 0 0 0 #4169E1;
          }
        `}</style>

        {/* Header using GlobalHeaderTitle */}
        <GlobalHeaderTitle
          title="Student Issues"
          subtitle={`${paginationData.total_items} total issues`}
          icon="pi pi-exclamation-triangle"
          buttons={[]}
        />

        {/* Table Container */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          width: '100%'
        }}>
          {loading ? (
            <StudentIssuesSkeletonLoader />
          ) : (
            <>
              {/* Info Bar */}
              {issues.length > 0 && (
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #f3f4f6',
                  background: '#fafbfc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(91, 124, 230, 0.08) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className="pi pi-list" style={{ fontSize: '14px', color: '#4169E1' }}></i>
                    </div>
                    <span style={{
                      fontSize: '13px',
                      color: '#64748b',
                      fontWeight: '600'
                    }}>
                      Showing {((page - 1) * paginationData.per_page) + 1} - {Math.min(page * paginationData.per_page, paginationData.total_items)} of {paginationData.total_items} issues
                    </span>
                  </div>

                  <div style={{
                    padding: '8px 16px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                    boxShadow: '0 4px 12px rgba(65, 105, 225, 0.3)'
                  }}>
                    <span style={{
                      fontSize: '13px',
                      color: 'white',
                      fontWeight: '700'
                    }}>
                      Page {paginationData.current_page} of {paginationData.total_pages}
                    </span>
                  </div>
                </div>
              )}

              {/* DataTable */}
              <DataTable
                value={issues}
                lazy
                rows={paginationData.per_page}
                first={(page - 1) * paginationData.per_page}
                totalRecords={paginationData.total_items}
                paginator={false}
                emptyMessage={
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: '400px'
                  }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '20px',
                      boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)'
                    }}>
                      <i className="pi pi-exclamation-triangle" style={{ fontSize: '25px', color: 'white' }}></i>
                    </div>
                    <h3 style={{
                      margin: 0,
                      color: '#1e40af',
                      fontSize: '20px',
                      fontWeight: '700',
                      marginBottom: '8px'
                    }}>
                      No Issues Found
                    </h3>
                    <p style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: '14px',
                      fontWeight: '500',
                      maxWidth: '400px',
                      lineHeight: 1.6
                    }}>
                      There are no student issues available at the moment.
                    </p>
                  </div>
                }
              >
                <Column header="#" body={serialNumberTemplate} />
                <Column header="Issue Title" body={titleTemplate} />
                <Column header="Type" body={issueTypeTemplate} />
                <Column header="Student" body={studentTemplate} />
                <Column header="Raised By" body={teacherTemplate} />
                <Column header="Date & Time" body={dateTimeTemplate} />
                <Column header="Status" body={statusTemplate} />
                <Column header="Actions" body={actionTemplate} />
              </DataTable>

              {/* CustomPaginator */}
              {paginationData.total_pages > 1 && (
                <CustomPaginator
                  page={paginationData.current_page}
                  totalPages={paginationData.total_pages}
                  totalRecords={paginationData.total_items}
                  pageSize={paginationData.per_page}
                  onPageChange={(newPage) => setPage(newPage)}
                  showInfoBar={false}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ViewStudentIssueModal
        open={isViewModalOpen}
        onClose={handleCloseModal}
        issueId={selectedIssueId}
      />
    </>
  );
};

export default StudentIssues;